import styled, { css } from "styled-components";
import { SpacingStyle, getMarginStyle, getPaddingStyle } from "core/styles/paddingMarginStyle";
import { scrollerStyle } from "core/styles/scrollerStyle";
import { OptionWrapperVariant } from "Dropdown";

const borderlessVariant = ({ theme }: { theme: { variant: "default" | "borderless" } }) =>
  theme?.variant === "borderless" &&
  css`
    .options-wrapper {
      border: 1.5px solid ${({ theme }) => theme?.colors?.gray.gray10};
      box-shadow: 0px 1px 3px 0px #0000001f;
      border-radius: 8px;
    }

    .options-item {
      font-family: Nunito-Sans-Bold;
      border-bottom: unset;

      svg {
        margin-right: 16px;
      }

      &:hover {
        background-color: ${({ theme }) => theme?.colors?.primary.surface};
        color: ${({ theme }) => theme?.colors?.primary.default};

        path {
          stroke: ${({ theme }) => theme?.colors?.primary.default};
        }
      }
    }
  `;

export const Container = styled.div<SpacingStyle>`
  position: relative;
  width: auto;

  ${getMarginStyle}
  ${getPaddingStyle}
  ${borderlessVariant}
`;

export const Header = styled.div<{
  padding?: string;
  borderRadius?: number;
  activeBorder?: boolean;
  height?: string;
  width?: string;
  disabled?: boolean;
  isError?: boolean;
  ref?: any;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: Nunito-Sans-Bold;
  padding: ${({ padding }) => padding || "4px 20px 4px 16px"};
  border-radius: ${({ borderRadius }) => (borderRadius ? `${borderRadius}px` : "4px")};
  border: ${({ activeBorder, isError, theme }) =>
    isError
      ? `1.5px solid ${theme?.colors?.danger.default}`
      : activeBorder
        ? `1.5px solid ${theme?.colors?.primary.tint4}`
        : `1.5px solid ${theme?.colors?.gray.gray10}`};
  color: ${({ theme }) => theme?.colors?.neutral.neutral90};
  cursor: pointer;
  background-color: ${({ theme }) => theme?.colors?.white};
  box-sizing: border-box;
  height: ${({ height }) => height || "auto"};
  width: ${({ width }) => width};
  gap: 8px;

  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme?.colors?.neutral.neutral30};
      border: 1px solid ${({ theme }) => theme?.colors?.neutral.neutral40};
      pointer-events: none;
    `}

  &:hover {
    border: 1.5px solid ${({ theme }) => theme?.colors?.primary.tint4};

    .clear-content-icon {
      visibility: ${({ disabled }) => (disabled ? "hidden" : "visible")};
    }
  }

  .clear-content-icon {
    margin-left: auto;
    visibility: hidden;
  }
`;

const alignmentSelector = ({ alignment }: { alignment?: "left" | "top" | "right" }) => {
  if (alignment === "left") {
    return css`
      left: 0;
    `;
  }

  if (alignment === "top") {
    return css`
      bottom: 120%;
    `;
  }

  return css`
    right: 0;
  `;
};

export const OptionsWrapper = styled.div<{
  minWidth?: number;
  alignment?: "left" | "top" | "right";
}>`
  position: absolute;
  box-shadow: 0px 5px 22px 0px #0f22511a;
  border-radius: 16px;
  z-index: 999;
  background-color: ${({ theme }) => theme?.colors?.white};
  margin-top: 4px;
  overflow: hidden;
  width: fit-content;
  min-width: ${({ minWidth }) => `${minWidth}px`};

  ${scrollerStyle};
  ${alignmentSelector};

  &::-webkit-scrollbar {
    width: 6px;
  }
`;

export const Option = styled.div<{
  disabled: boolean;
  selected: boolean;
  optionAutoWidth?: boolean;
}>`
  display: flex;
  cursor: pointer;
  padding: 10px 16px;
  font-family: Nunito-Sans-SemiBold;
  color: ${({ theme }) => theme?.colors?.neutral.neutral90};
  border-bottom: 1px solid ${({ theme }) => theme?.colors?.neutral.neutral30};
  min-width: ${({ optionAutoWidth }) => (optionAutoWidth ? "auto" : "200px")};
  align-items: center;
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
  justify-content: flex-start;
  background: ${({ selected, theme }) =>
    selected ? theme?.colors?.primary.surface : theme?.colors?.white};
  color: ${({ disabled, selected, theme }) =>
    selected
      ? theme?.colors?.primary.default
      : disabled
        ? theme?.colors?.neutral.neutral60
        : theme?.colors?.black};

  &:hover {
    font-family: Nunito-Sans-Bold;
  }

  &:last-child {
    border-bottom: 0px;
  }
`;

export const ErrorMessage = styled.div<{ errorPosition?: string }>`
  font-family: Nunito-Sans-SemiBold;
  font-size: 12px;
  margin-top: 2px;
  color: ${({ theme }) => theme?.colors?.dangerv2.default};
  float: ${({ errorPosition }) => errorPosition};
`;

export const OptionScrollerContainer = styled.div<{ optionWrapperVariant?: OptionWrapperVariant }>`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  max-height: ${({ optionWrapperVariant }) =>
    optionWrapperVariant === "drawer" ? "none" : "210px"};

  ${scrollerStyle}
`;
